// @react
import React from 'react'
// @types
import type { ReactElement } from 'react'
import type { GetFeaturedBenefitQuery } from 'lib/graphql/graphqlTypes'
import type { Theme } from '@material-ui/core/styles'
// @graphql
import { GET_FEATURED_BENEFIT } from 'graphql/queries/featuredBenefit'
// @libraries
import { makeStyles } from '@material-ui/core/styles'
import { useMediaQuery } from '@material-ui/core'
import { useQuery } from '@apollo/client'
import Shiitake from 'shiitake'
// @config
import { contentTypes } from 'lib/constants/contentTypes'
import { contentLabels } from 'modules/benefitsCenter/components/FeaturedBenefit/contentLabels'
// @common
import Colors from '@bit/atd.web.colors'
import { createURL } from 'modules/search/pages/LayoutWrapper'
// @components
import Grid from '@bit/atd.web.grid'
import Container from '@bit/atd.web.container'
import Box from '@bit/atd.web.box'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
import Typography from '@material-ui/core/Typography'
import Button from '@bit/atd.web.button'
import CustomCardHit from 'modules/search/components/Widgets/CustomCardHit'
import LinearProgressBar from '@bit/atd.web.linear-progress-bar'
import QueryError from 'modules/common/components/Errors/QueryError'

const createSearchLink = (benefitContentTypes: {
  [key: string]: string | null
}) => {
  const index =
    contentTypes.find(({ label }) => label === benefitContentTypes.lvl0)
      ?.index || ''

  const { __typename, ...benefitTypes } = benefitContentTypes
  const filter = Object.values(benefitTypes).filter((item) => item !== null)
  const searchState = {
    hierarchicalMenu: {
      'contentTypes.lvl0': filter[filter.length - 1],
    },
  }
  return `${process.env.PUBLIC_URL}/search/${index}${createURL(
    searchState,
  )}&toggle%5BmemberBenefit%5D=true&page=1`
}

const contentTypeFormatter = (contentTypes: string[]): string => {
  const type = ['lvl2', 'lvl1', 'lvl0'].reduce((acc, item: any) => {
    if (acc) return acc
    const types = contentTypes[item]?.split('>')
    return types ? types[types.length - 1].replace(' ', '') : ''
  }, '')

  const contentLabel = contentLabels.find(({ key }) => key === type)
  return contentLabel?.label || ''
}

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    padding: '24px 0px',
    backgroundColor: Colors.white,
    [theme.breakpoints.up('md')]: {
      padding: '32px 0px',
    },
  },
  containerWrapper: {
    '& > .MuiGrid-item:first-child': {
      [theme.breakpoints.up('md')]: {
        paddingRight: '15px',
      },
    },
    '& > .MuiGrid-item:last-child': {
      [theme.breakpoints.up('md')]: {
        alignSelf: 'center',
        paddingLeft: '30px',
      },
    },
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '24px',
    color: '2E2D31',
    marginBottom: '4px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '28px',
    color: '#000',
    marginBottom: '8px',
  },
  description: {
    lineHeight: '24px',
    color: '#000',
    marginBottom: '0',
  },
  cardsContainer: {
    [theme.breakpoints.down('sm')]: {
      marginTop: '24px',
    },
    '& footer': {
      backgroundColor: Colors.gray200,
    },
  },
  button: {
    marginTop: '28px',
    [theme.breakpoints.up('md')]: {
      marginTop: '16px',
    },
  },
}))

const FeaturedBenefit = (): ReactElement => {
  const isDesctop = useMediaQuery('(min-width: 1024px)')

  /******************************************
   * HOOKS
   ******************************************/

  const {
    loading: isLoading,
    error: isError,
    data,
  } = useQuery<GetFeaturedBenefitQuery>(GET_FEATURED_BENEFIT, {
    fetchPolicy: 'network-only',
  })

  const classes = useStyles()

  /******************************************
   * FUNCTIONS
   ******************************************/

  if (isLoading) {
    return <SkeletonLoader />
  }

  if (isError || !data) {
    return <QueryError />
  }

  const { title, subTitle } = data.featuredBenefit

  const benefit = data.featuredBenefit.benefit as any
  const link = createSearchLink(benefit.contentTypes)

  const renderBtn = () => (
    <Button className={classes.button} href={link} variant="secondary">
      {`View All ${contentTypeFormatter(benefit.contentTypes)}`}
    </Button>
  )

  /******************************************
   * RENDER
   ******************************************/

  return (
    <>
      <Box variant="benefit" classNames={{ root: classes.wrapper }}>
        <Container>
          <>
            <Grid className={classes.containerWrapper} container>
              {/* Title / Description */}
              <Grid item xs={12} md={6}>
                <Typography
                  className={classes.label}
                  component={'p'}
                  variant="body1"
                >
                  FEATURED BENEFIT
                </Typography>
                <Typography
                  className={classes.title}
                  component={'h5'}
                  variant="h5"
                >
                  {title}
                </Typography>
                <Typography
                  className={classes.description}
                  component={'p'}
                  variant="subtitle1"
                >
                  <Shiitake
                    lines={5}
                    throttleRate={400}
                    tagName="span"
                    attributes={{ title: subTitle as string }}
                  >
                    {subTitle}
                  </Shiitake>
                </Typography>
                {isDesctop && renderBtn()}
              </Grid>

              {/* Custom Card Hit */}
              <Grid item xs={12} md={6} className={classes.cardsContainer}>
                <Grid container>
                  <CustomCardHit
                    key={benefit.id}
                    hit={{ ...benefit, objectID: benefit.id }}
                    index={{ targetedIndex: 'master' }}
                    layout={'featured'}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Button */}
            {!isDesctop && renderBtn()}
          </>
        </Container>
      </Box>
      <LinearProgressBar />
    </>
  )
}

export default FeaturedBenefit
